import {
  Badge,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { SilometrixThresholdListService } from '@old/services/domain/device/silometrix-threshold-list';
import { GridColDef } from '@mui/x-data-grid-pro';
import { BaseTable } from '@old/ui/table';
import DangerCycle from '@old/assets/svg/orange-cycle.svg';
import CautionCycle from '@old/assets/svg/yellow-cycle.svg';
import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { useEffectOnUpdate } from '@old/hooks/core';
import i18n from '../../../../old/libs/language';
import { configButtonStylesContained } from '@old/styles/generic-styles';
import { SilometrixThresholdDefaultService } from '@old/services/domain/device/silometrix-threshold-default';

type SilometrixThreshold = Awaited<ReturnType<typeof SilometrixThresholdListService>>['summary']['response'];

type SilometrixMetricsAlerts = SilometrixThreshold['metrics_alerts_configs'];

type SilometrixDefaultThreshold = Awaited<
  ReturnType<typeof SilometrixThresholdDefaultService>
>['summary']['response'];

type SilometrixThresholdType = {
  silometrixData: SilometrixMetricsAlerts;
  buttonResetValue: boolean;
  setResetButtonValue: (newValue?: boolean) => void;
  setHegRows: Dispatch<SetStateAction<SilometrixMetricsAlerts>>;
  setCo2Rows: Dispatch<SetStateAction<SilometrixMetricsAlerts>>;
  hegRows: SilometrixMetricsAlerts;
  co2Rows: SilometrixMetricsAlerts;
  validation: {
    ONLY_NUMS: (value: string) => boolean;
    FLOAT_NUMS: (value: string) => boolean;
    TYPING_FLOAT_NUMS: (value: string) => boolean;
    BETWEEN_0_AND_100: (value: string) => boolean;
  };
  hegDefaultValues: SilometrixDefaultThreshold | undefined;
  co2DefaultValues: SilometrixDefaultThreshold | undefined;
  setFormHasErrors: Dispatch<SetStateAction<boolean>>;
};

type DynamicSliderProps = {
  railGradient: string;
};

const DynamicSlider = styled(Slider)<DynamicSliderProps>(({ railGradient }) => ({
  color: 'transparent',
  marginRight: 10,
  height: 8,
  '& .MuiSlider-track': {
    display: 'none',
  },
  '& .MuiSlider-rail': {
    background: railGradient,
    opacity: 1,
    height: 8,
  },
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    '&[data-index="0"]': {
      backgroundColor: '#6baa00 !important',
    },
    '&[data-index="1"]': {
      backgroundColor: '#6baa00 !important',
    },
    '&[data-index="2"]': {
      backgroundColor: '#ffff00 !important',
    },
    '&[data-index="3"]': {
      backgroundColor: '#fd7e14 !important',
    },
    '&:focus, &:hover, &.Mui-active': {
      boxShadow: '0 0 0 8px rgba(0, 0, 0, 0.16)',
    },
  },
  '& .MuiSlider-mark': {
    backgroundColor: 'transparent',
    width: '8px',
    height: '8px',
  },
  '& .MuiSlider-mark[data-index="0"]': {
    backgroundColor: '#6baa00 !important',
  },
  '& .MuiSlider-mark[data-index="1"]': {
    backgroundColor: '#6baa00 !important',
  },
  '& .MuiSlider-mark[data-index="2"]': {
    backgroundColor: '#ffcc00 !important',
  },
  '& .MuiSlider-mark[data-index="3"]': {
    backgroundColor: '#fd7e14 !important',
  },
}));

export function SilometrixThresholdTable({
  silometrixData,
  buttonResetValue,
  setResetButtonValue,
  setHegRows,
  setCo2Rows,
  hegRows,
  co2Rows,
  validation: VALIDATION,
  hegDefaultValues,
  co2DefaultValues,
  setFormHasErrors,
}: SilometrixThresholdType) {
  const HEG_METRIC = 5;
  const CO2_METRIC = 3;

  const MIN_VALUE_METRIC = 'min_value';
  const MAX_VALUE_METRIC = 'max_value';

  const [selectedMetric, setSelectedMetric] = useState(HEG_METRIC);

  const [hasErrorsHEG, setHasErrorsHEG] = useState(false);
  const [hasErrorsCO2, setHasErrorsCO2] = useState(false);
  const [hegErrorCount, setHegErrorCount] = useState(0);
  const [co2ErrorCount, setCo2ErrorCount] = useState(0);

  const uniqueSpeciesID = Array.from(new Set(silometrixData?.map((sb) => sb.silobag_species_type_id)));

  const KEYS = {
    CO2_MIN_VALUE: '_co2_min_value',
    CO2_MAX_VALUE: '_co2_max_value',
    HEG_MIN_VALUE: '_heg_min_value',
    HEG_MAX_VALUE: '_heg_max_value',
  };

  const initErrorData: Record<string, string> = uniqueSpeciesID.reduce((rec, id) => {
    return {
      ...rec,
      [id + KEYS.CO2_MIN_VALUE]: '',
      [id + KEYS.CO2_MAX_VALUE]: '',
      [id + KEYS.HEG_MIN_VALUE]: '',
      [id + KEYS.HEG_MAX_VALUE]: '',
    };
  }, {});

  const [errorData, setErrorData] = useState(initErrorData);

  useEffectOnUpdate(() => {
    setErrorData(initErrorData);
    setResetButtonValue(true);
  }, [buttonResetValue]);

  useEffectOnUpdate(() => {
    const hegKeys = Object.keys(errorData).filter((key) =>
      [KEYS.HEG_MIN_VALUE, KEYS.HEG_MAX_VALUE].some((metricKey) => key.endsWith(metricKey))
    );

    const co2Keys = Object.keys(errorData).filter((key) =>
      [KEYS.CO2_MIN_VALUE, KEYS.CO2_MAX_VALUE].some((metricKey) => key.endsWith(metricKey))
    );

    const hasErrorHEG = hegKeys.some((key) => errorData[key] !== '');
    const hasErrorCO2 = co2Keys.some((key) => errorData[key] !== '');

    setHasErrorsHEG(hasErrorHEG);
    setHasErrorsCO2(hasErrorCO2);

    const hasErrors = Object.keys(initErrorData).some((key) => errorData[key] !== initErrorData[key]);

    setFormHasErrors(hasErrors);

    const hegErrors = hegKeys.filter((key) => errorData[key] !== '').length;
    const co2Errors = co2Keys.filter((key) => errorData[key] !== '').length;

    setHegErrorCount(hegErrors);
    setCo2ErrorCount(co2Errors);
  }, [errorData]);

  const handlerReturnOriginalHegValues = () => {
    const updatedHegRows = hegRows.map((row) => {
      const defaultValue = hegDefaultValues?.find(
        (def) =>
          def.metric_id === row.metric_id && def.silobag_species_type_id === row.silobag_species_type_id
      );
      if (defaultValue) {
        return {
          ...row,
          min_value: defaultValue.min_value,
          max_value: defaultValue.max_value,
        };
      }
      return row;
    });

    setHegRows(updatedHegRows);

    const resetHegValues: Record<string, string> = uniqueSpeciesID.reduce((rec, id) => {
      return {
        ...rec,
        [id + KEYS.HEG_MIN_VALUE]: '',
        [id + KEYS.HEG_MAX_VALUE]: '',
      };
    }, {});
    setErrorData((prevErrorData) => ({
      ...prevErrorData,
      ...resetHegValues,
    }));
  };

  const handlerReturnOriginalCo2Values = () => {
    const updatedCo2Rows = co2Rows.map((row) => {
      const defaultValue = co2DefaultValues?.find(
        (def) =>
          def.metric_id === row.metric_id && def.silobag_species_type_id === row.silobag_species_type_id
      );
      if (defaultValue) {
        return {
          ...row,
          min_value: defaultValue.min_value,
          max_value: defaultValue.max_value,
        };
      }
      return row;
    });

    setCo2Rows(updatedCo2Rows);

    const resetCo2Values: Record<string, string> = uniqueSpeciesID.reduce((rec, id) => {
      return {
        ...rec,
        [id + KEYS.CO2_MIN_VALUE]: '',
        [id + KEYS.CO2_MAX_VALUE]: '',
      };
    }, {});

    setErrorData((prevErrorData) => ({
      ...prevErrorData,
      ...resetCo2Values,
    }));
  };

  const RESET_LIST_BY_METRIC: Record<number, () => void> = {
    [CO2_METRIC]: handlerReturnOriginalCo2Values,
    [HEG_METRIC]: handlerReturnOriginalHegValues,
  };

  const MIN_METRIC_KEY_MAP: Record<number, string> = {
    [CO2_METRIC]: KEYS.CO2_MIN_VALUE,
    [HEG_METRIC]: KEYS.HEG_MIN_VALUE,
  };

  const MAX_METRIC_KEY_MAP: Record<number, string> = {
    [CO2_METRIC]: KEYS.CO2_MAX_VALUE,
    [HEG_METRIC]: KEYS.HEG_MAX_VALUE,
  };

  const minMetricKey = MIN_METRIC_KEY_MAP[selectedMetric];
  const maxMetricKey = MAX_METRIC_KEY_MAP[selectedMetric];

  const METRIC_LIST_MAP: Record<number, NonNullable<typeof co2Rows>> = {
    [CO2_METRIC]: co2Rows!,
    [HEG_METRIC]: hegRows!,
  };

  const LIST_SETTER_MAP: Record<
    number,
    Dispatch<SetStateAction<SilometrixThresholdType['silometrixData']>>
  > = {
    [CO2_METRIC]: setCo2Rows,
    [HEG_METRIC]: setHegRows,
  };

  const handlerOnchangeMinValues = (newValue: string, speciesID: number, rowID: number) => {
    const selectedMetricList = METRIC_LIST_MAP[selectedMetric];
    const setterList = LIST_SETTER_MAP[selectedMetric];
    const foundedRow = selectedMetricList.find((row) => row.id === rowID)!;
    const newValueNum = parseFloat(newValue ? newValue : '0');

    if (VALIDATION.TYPING_FLOAT_NUMS(newValue)) {
      const updatedRow = selectedMetricList?.map((row) =>
        row.id === rowID ? { ...row, [MIN_VALUE_METRIC]: newValue } : row
      );
      setterList(updatedRow);
    }

    const maxValue = parseFloat(foundedRow.max_value as string);

    const isPositiveFloat = VALIDATION.BETWEEN_0_AND_100(newValue);
    const isLessOrEqualThan100 = newValueNum <= 100;
    const isLessThanMaxValue = newValueNum < maxValue;
    const isGreaterThan0 = newValueNum > 0;

    if (isPositiveFloat && isLessOrEqualThan100 && isLessThanMaxValue && isGreaterThan0) {
      setErrorData((prev) => ({ ...prev, [speciesID + minMetricKey]: '' }));

      if (maxValue <= 100 && maxValue > 0) {
        setErrorData((prev) => ({ ...prev, [speciesID + maxMetricKey]: '' }));
      }

      return;
    }

    if (!isPositiveFloat) {
      const errorMsg = i18n.t('app.silometrix_threshold.up_to_two_decimals');
      setErrorData((prev) => ({ ...prev, [speciesID + minMetricKey]: errorMsg }));

      return;
    }

    if (!isLessOrEqualThan100) {
      const errorMsg = i18n.t('app.silometrix_threshold.max_100');
      setErrorData((prev) => ({ ...prev, [speciesID + minMetricKey]: errorMsg }));

      return;
    }

    if (!isGreaterThan0) {
      const errorMsg = i18n.t('app.silometrix_threshold.cant_be_0');
      setErrorData((prev) => ({ ...prev, [speciesID + minMetricKey]: errorMsg }));

      return;
    }

    if (!isLessThanMaxValue) {
      const errorMsg = i18n.t('app.silometrix_threshold.min_more_max');
      setErrorData((prev) => ({ ...prev, [speciesID + minMetricKey]: errorMsg }));

      return;
    }
  };

  const handlerOnchangeMaxValues = (newValue: string, speciesID: number, rowID: number) => {
    const selectedMetricList = METRIC_LIST_MAP[selectedMetric];
    const newValueNum = parseFloat(newValue ? newValue : '0');
    const setterList = LIST_SETTER_MAP[selectedMetric];

    const foundedRow = selectedMetricList.find((row) => row.id === rowID)!;

    if (VALIDATION.TYPING_FLOAT_NUMS(newValue)) {
      const updatedRow = selectedMetricList?.map((row) =>
        row.id === rowID ? { ...row, [MAX_VALUE_METRIC]: newValue } : row
      );

      setterList(updatedRow);
    }

    const minValue = parseFloat(foundedRow.min_value as string);

    const isPositiveFloat = VALIDATION.BETWEEN_0_AND_100(newValue);
    const isLessOrEqualThan100 = newValueNum <= 100;
    const isGreaterThanMinValue = newValueNum > minValue;
    const isGreaterThan0 = newValueNum > 0;

    if (isPositiveFloat && isLessOrEqualThan100 && isGreaterThanMinValue && isGreaterThan0) {
      setErrorData((prev) => ({ ...prev, [speciesID + maxMetricKey]: '' }));

      if (minValue < 100 && minValue > 0) {
        setErrorData((prev) => ({ ...prev, [speciesID + minMetricKey]: '' }));
      }

      return;
    }

    if (!isPositiveFloat) {
      const errorMsg = i18n.t('app.silometrix_threshold.up_to_two_decimals');
      setErrorData((prev) => ({ ...prev, [speciesID + maxMetricKey]: errorMsg }));
      return;
    }

    if (!isLessOrEqualThan100) {
      const errorMsg = i18n.t('app.silometrix_threshold.max_100');
      setErrorData((prev) => ({ ...prev, [speciesID + maxMetricKey]: errorMsg }));

      return;
    }

    if (!isGreaterThan0) {
      const errorMsg = i18n.t('app.silometrix_threshold.cant_be_0');
      setErrorData((prev) => ({ ...prev, [speciesID + maxMetricKey]: errorMsg }));

      return;
    }

    if (!isGreaterThanMinValue) {
      const errorMsg = i18n.t('app.silometrix_threshold.min_more_max');
      setErrorData((prev) => ({ ...prev, [speciesID + maxMetricKey]: errorMsg }));

      return;
    }
  };

  const handleChangeList = (val: ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(val.target.value);
    setSelectedMetric(value);
  };

  const columns: GridColDef[] = [
    {
      field: 'species_type_description',
      display: 'flex',
      width: 130,
      renderHeader: () => (
        <Typography color={'#49454F'} fontSize={13} fontWeight={600}>
          {i18n.t('app.species')}
        </Typography>
      ),
      renderCell: (params) => {
        const speciesName = params.value;
        return <Box>{speciesName}</Box>;
      },
    },
    {
      field: 'metric_id',
      display: 'flex',
      width: 80,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <Typography color={'#49454F'} fontSize={13} fontWeight={600}>
          {i18n.t('app.variable')}
        </Typography>
      ),
      renderCell: (params) => {
        const metricName = params.value == 3 ? 'CO2' : 'HEG';

        return <Box>{metricName}</Box>;
      },
    },

    {
      field: 'min_value',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 210,
      align: 'center',
      renderHeader: () => (
        <Typography color={'#49454F'} fontSize={13} fontWeight={600}>
          {i18n.t('app.silometrix_threshold.minimun_caution_range')} <img src={CautionCycle} alt="caution" />
        </Typography>
      ),
      renderCell: (params) => {
        const error = errorData[params.row.silobag_species_type_id + minMetricKey];

        return (
          <Box display="flex" flexDirection="column" alignItems="center" width="100%">
            <TextField
              size="small"
              value={params.value}
              onChange={(e) =>
                handlerOnchangeMinValues(e.target.value, params.row.silobag_species_type_id, params.row.id)
              }
              error={Boolean(error)}
            />
            <Typography
              variant="caption"
              color={error ? 'error' : 'textSecondary'}
              sx={{
                mt: 0.5,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                fontSize: 12,
              }}
            >
              {error || ' '}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'max_value',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 180,
      align: 'center',
      renderHeader: () => (
        <Typography color={'#49454F'} fontSize={13} fontWeight={600}>
          {i18n.t('app.silometrix_threshold.minimun_critic_range')} <img src={DangerCycle} alt="danger" />
        </Typography>
      ),
      renderCell: (params) => {
        const error = errorData[params.row.silobag_species_type_id + maxMetricKey];

        return (
          <Box display="flex" flexDirection="column" alignItems="center" width="100%">
            <TextField
              size="small"
              value={params.value}
              onChange={(e) =>
                handlerOnchangeMaxValues(e.target.value, params.row.silobag_species_type_id, params.row.id)
              }
              error={Boolean(error)}
            />
            <Typography
              variant="caption"
              color={error ? 'error' : 'textSecondary'}
              sx={{
                mt: 0.5,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                fontSize: 12,
              }}
            >
              {error || ' '}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: 'values',
      display: 'flex',
      flex: 1,
      sortable: false,
      minWidth: 700,
      align: 'center',
      renderHeader: () => <span />,
      renderCell: (params) => {
        const minValue = params.row.min_value;
        const maxValue = params.row.max_value;
        const marks = [
          {
            value: 0,
            label: '0%',
          },
          {
            value: 33,
            label: `${minValue}%`,
          },
          {
            value: 66,
            label: `${maxValue}%`,
          },
          {
            value: 100,
            label: '100%',
          },
        ];

        const railGradient = `
        linear-gradient(
          to right,
          green 0%,
          green ${(33 / 100) * 100}%,
          yellow ${(33 / 100) * 100}%,
          yellow ${(66 / 100) * 100}%,
          orange ${(66 / 100) * 100}%,
          orange 50%
        )
      `;

        return (
          <DynamicSlider
            value={[0, 100, 33, 66]}
            marks={marks}
            step={25}
            max={100}
            min={0}
            railGradient={railGradient}
            disabled
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#FFFFFF' }} margin={3} padding={2}>
      <Box display={'flex'} justifyContent={'space-between'}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} display={'flex'} alignItems={'center'}>
          <Typography variant="h5" fontWeight={500} sx={{ color: '#49454F' }}>
            {i18n.t('app.silometrix_threshold.title')}
          </Typography>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={selectedMetric}
              onChange={handleChangeList}
            >
              <Badge badgeContent={hegErrorCount} color="error">
                <FormControlLabel
                  value={HEG_METRIC}
                  control={<Radio color={hasErrorsHEG ? 'error' : 'success'} />}
                  label="HEG"
                />
              </Badge>

              <Badge badgeContent={co2ErrorCount} color="error">
                <FormControlLabel
                  sx={{ marginLeft: 1 }}
                  value={CO2_METRIC}
                  control={<Radio color={hasErrorsCO2 ? 'error' : 'success'} />}
                  label="CO2"
                />
              </Badge>
            </RadioGroup>
          </FormControl>
        </Stack>
        <Button
          variant="contained"
          size="medium"
          sx={configButtonStylesContained}
          onClick={RESET_LIST_BY_METRIC[selectedMetric]}
        >
          {i18n.t('app.silometrix_threshold.reset_list_values')}
        </Button>
      </Box>

      {METRIC_LIST_MAP[selectedMetric] && (
        <Grid container sx={{ width: '100%', marginTop: '20px' }}>
          <Grid item xs={12}>
            <BaseTable
              rows={METRIC_LIST_MAP[selectedMetric] ?? []}
              columns={columns}
              initialPageSize={50}
              // rowHeight={250}
              getRowHeight={() => 100}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
